import React, { useState } from 'react';
import { first } from 'lodash';
import { ScaleTime } from 'd3-scale';
import { Bar, Line } from '@visx/shape';
import { GridColumns, GridRows } from '@visx/grid';
import { AxisLeft, AxisRight } from '@visx/axis';
import { LinearGradient } from '@visx/gradient';
import { getStringWidth } from '@visx/text';
import { common, model } from '@gosupersimple/types';

import { localPoint } from '@visx/event';

import { notNil } from '@/lib/utils';
import { formatPropertyValue } from '@/explore/utils/format';
import { findClosestDate, getFormattedDateForPeriod } from '@/lib/date';
import { ChartType, TimeAggregationPeriod } from '@/explore/types';
import { GroupFilter } from '@/explore/utils/drilldown';
import { InteractableProps } from '@/explore/utils/interactable';

import { ChartTooltip, ChartTooltipItem, detectHoveredSeries, renderGridColumns } from '../common';
import {
  getBarSeriesPosition,
  getScaleTimeBandwidth,
  buildTooltipData,
  getTickValues,
  getHighlightedTickValues,
} from './utils';
import { SeriesMetaData, TimeSeriesData } from '../grouped-chart/types';
import { BarSeries } from './bar-series';
import { LineSeries } from './line-series';
import { AreaSeries } from './area-series';
import { SeriesValues } from './series-values';
import { getScreenPoint, increaseBrightness, sortTooltipData } from '../utils';
import {
  explodeCombinedKey,
  getLeftRightAxisFormats,
  getLeftRightAxisTypes,
  isCombinedKey,
} from '../grouped-chart/utils';
import { SeriesValueDot } from './series-value-dot';
import { NonOverlappingValueLabels } from '../non-overlapping-value-labels';
import { convertTicksToScale } from '../grouped-chart/utils/scale';

import { useValueScales } from './use-value-scales';

import commonStyles, {
  gridLineColor,
  gridHighlightedLineColor,
  hoverLineColor,
  graphVerticalPadding,
} from '../charts.module.scss';

export const NumYTicks = 7;
const GraphVerticalPadding = parseInt(graphVerticalPadding);
const TooltipItemsLimit = 40;
const YValuePaddingCoef = 0.2;

export interface ChartColor {
  fill: string;
  highlight: string;
}

interface TimeSeriesChartProps {
  width: number;
  height: number;
  data: TimeSeriesData;
  dateScale: ScaleTime<number, number>;
  zonedDateScale: ScaleTime<number, number>;
  aggPeriod: TimeAggregationPeriod | null;
  highlightedDate: Date | null;
  highlightedSeriesKey?: string | null;
  groupKey: string | undefined;
  onMouseLeave?: () => void;
  onTimeClick?: (params: {
    x: number;
    y: number;
    key: string;
    groupFilters: GroupFilter[];
  }) => void;
  onDateHighlight: (date: Date | null) => void;
  hideGrid: boolean;
  timezone: string;
}

export const TimeSeriesChart = (props: TimeSeriesChartProps) => {
  const {
    width,
    height,
    data,
    dateScale,
    zonedDateScale,
    aggPeriod,
    highlightedDate,
    highlightedSeriesKey,
    groupKey,
    onTimeClick,
    onDateHighlight,
    hideGrid,
    timezone,
  } = props;

  const hasOnlyBars = data.series.every(({ chartType }) => chartType === 'bar');
  const hasBars = hasOnlyBars || data.series.some(({ chartType }) => chartType === 'bar');
  const bandwidth = getScaleTimeBandwidth(dateScale, aggPeriod);

  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);

  const axisTypes = getLeftRightAxisTypes(data.series);
  const axisFormats = getLeftRightAxisFormats(data.series);

  const valueScales = useValueScales({
    data,
    height,
    padding: YValuePaddingCoef,
    numTicks: NumYTicks,
  });

  const formatLeftAxisValue = (n: number) => {
    const value = axisTypes.left === 'Integer' ? Math.round(n) : Math.round(n * 100) / 100;
    return formatPropertyValue(value, {
      type: axisTypes.left,
      format: axisFormats.left,
      style: 'compact',
    });
  };

  const formatRightAxisValue = (n: number) => {
    const value = axisTypes.right === 'Integer' ? Math.round(n) : Math.round(n * 100) / 100;
    return formatPropertyValue(value, {
      type: axisTypes.right,
      format: axisFormats.right,
      style: 'compact',
    });
  };

  const yAxisWidths = {
    left: Math.max(
      ...valueScales.left
        .ticks(NumYTicks)
        .map((i) => formatLeftAxisValue(i.valueOf()))
        .map((i) => getStringWidth(i) ?? 0),
    ),
  };

  const handleMouseMove = (
    event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>,
  ) => {
    const point = localPoint(event);
    if (point === null) {
      return;
    }

    const hoveredDate = dateScale.invert(point.x - bandwidth / 2);
    const dates = data.items.map((item) => item.dateValue);
    const closestDate = findClosestDate(dates, hoveredDate);

    if (!closestDate) {
      return;
    }

    onDateHighlight(closestDate);

    const dataItem = data.items.find(
      ({ dateValue }) => dateValue.getTime() === closestDate.getTime(),
    );

    if (dataItem === undefined) {
      return setTooltipData(null);
    }

    // Only detect if not already provided via group parameter
    // Handle both regular and grouped data
    const values = Array.isArray((dataItem as any).values)
      ? {}
      : ((dataItem as any).values as Record<string, number | null>);
    const seriesData = data.series.map((s) => ({
      key: s.key,
      value: values[s.key] ?? null,
      chartType: s.chartType as 'line' | 'area' | 'bar',
      axis: s.axis,
    }));

    const barSeries = seriesData.filter((s) => s.chartType === 'bar');
    const barPositions = barSeries.map((barData, idx) => {
      const { barWidth, x } = getBarSeriesPosition(dateScale, aggPeriod, barSeries.length, idx);
      return {
        key: barData.key,
        x: dateScale(closestDate) + x,
        width: barWidth,
      };
    });

    // Detect which series is being hovered based on mouse Y position
    const seriesKey = detectHoveredSeries(point.y, seriesData, valueScales, point.x, barPositions);

    setTooltipData(
      buildTooltipData(
        dataItem,
        getScreenPoint(point, event.target as SVGElement),
        data,
        timezone,
        aggPeriod,
        seriesKey,
      ),
    );
  };

  const renderBarSeriesItem = (
    seriesItem: SeriesMetaData,
    barSeriesIdx: number,
    numBarSeries: number,
  ) => {
    const seriesData = data.items.map((item) => ({
      date: item.dateValue,
      value: item.values[seriesItem.key],
    }));

    const { barWidth, x } = getBarSeriesPosition(
      dateScale,
      aggPeriod,
      numBarSeries,
      barSeriesIdx++,
    );

    const activeHighlightKey = highlightedSeriesKey ?? tooltipData?.hoveredSeriesKey;
    const isHovered = activeHighlightKey === seriesItem.key;
    const hasSomeSeriesHovered = activeHighlightKey !== undefined;

    return (
      <BarSeries
        key={`${seriesItem.key}-${barSeriesIdx}`}
        data={seriesData}
        fillColor={seriesItem.color}
        fillOpacity={hasSomeSeriesHovered && !isHovered ? 0.3 : 0.9}
        valueScale={valueScales[seriesItem.axis]}
        dateScale={dateScale}
        barWidth={barWidth}
        x={x}
      />
    );
  };

  const renderLineSeriesItem = (seriesItem: SeriesMetaData) => {
    const seriesData = data.items.map((item) => ({
      date: item.dateValue,
      value: item.values[seriesItem.key],
    }));

    const activeHighlightKey = highlightedSeriesKey ?? tooltipData?.hoveredSeriesKey;
    const isHovered = activeHighlightKey === seriesItem.key;
    const hasSomeSeriesHovered = activeHighlightKey !== undefined;

    return (
      <LineSeries
        key={seriesItem.key}
        data={seriesData}
        strokeColor={seriesItem.color}
        strokeOpacity={hasSomeSeriesHovered && !isHovered ? 0.3 : 1}
        valueScale={valueScales[seriesItem.axis]}
        dateScale={dateScale}
      />
    );
  };

  const renderSeriesValueDots = () => {
    // Only render when there is a single date value
    if (data.items.length !== 1) {
      return null;
    }
    // Only render dots for line and area series
    const series = data.series.filter(
      ({ chartType }) => chartType === 'line' || chartType === 'area',
    );
    return series.map(({ key, axis, color }) =>
      data.items.map((item) => (
        <SeriesValueDot
          key={key}
          cx={dateScale(item.dateValue)}
          cy={valueScales[axis](item.values[key] ?? 0)}
          color={color}
        />
      )),
    );
  };

  const renderAreaSeriesItem = (seriesItem: SeriesMetaData) => {
    const seriesData = data.items.map((item) => ({
      date: item.dateValue,
      value: item.values[seriesItem.key],
    }));

    const activeHighlightKey = highlightedSeriesKey ?? tooltipData?.hoveredSeriesKey;
    const isHovered = activeHighlightKey === seriesItem.key;
    const hasSomeSeriesHovered = activeHighlightKey !== undefined;

    return (
      <AreaSeries
        key={seriesItem.key}
        data={seriesData}
        fillColor={seriesItem.color}
        strokeColor={increaseBrightness(seriesItem.color, 20)}
        opacity={hasSomeSeriesHovered && !isHovered ? 0.5 : 1}
        valueScale={valueScales[seriesItem.axis]}
        dateScale={dateScale}
      />
    );
  };

  const renderSeries = () => {
    let barSeriesIdx = 0;
    const numBarSeries = data.series.filter(({ chartType }) => chartType === 'bar').length;
    return data.series.map((series) => {
      switch (series.chartType) {
        case 'bar':
          return renderBarSeriesItem(series, barSeriesIdx++, numBarSeries);
        case 'line':
          return renderLineSeriesItem(series);
        case 'area':
          return renderAreaSeriesItem(series);
        default:
          return null;
      }
    });
  };

  const renderSeriesValues = () => {
    const numBarSeries = data.series.filter(({ chartType }) => chartType === 'bar').length;
    let barSeriesIdx = 0;
    return (
      <NonOverlappingValueLabels>
        {data.series.map((series) => {
          const seriesData = data.items.map((item) => ({
            date: item.dateValue,
            value: item.values[series.key],
          }));
          switch (series.chartType) {
            case 'bar': {
              if (!series.showValues) {
                barSeriesIdx++;
                return null;
              }
              const { barWidth, x } = getBarSeriesPosition(
                dateScale,
                aggPeriod,
                numBarSeries,
                barSeriesIdx++,
              );
              return (
                <SeriesValues
                  key={series.key}
                  series={series}
                  data={seriesData}
                  valueScale={valueScales[series.axis]}
                  dateScale={dateScale}
                  x={x + barWidth / 2}
                />
              );
            }
            case 'line':
            case 'area': {
              if (!series.showValues) {
                return null;
              }
              return (
                <SeriesValues
                  key={series.key}
                  series={series}
                  data={seriesData}
                  valueScale={valueScales[series.axis]}
                  dateScale={dateScale}
                  avoidOverflow={numBarSeries === 0}
                />
              );
            }
            default:
              return null;
          }
        })}
      </NonOverlappingValueLabels>
    );
  };

  const renderHighlightedPeriod = () => {
    if (!highlightedDate) {
      return null;
    }

    return (
      <rect
        x={dateScale(highlightedDate) - bandwidth / 2}
        y={0}
        width={bandwidth}
        height={height}
        fill={gridLineColor}
        pointerEvents="none"
      />
    );
  };

  const domainSizes = {
    left: valueScales.left.domain()[1] - valueScales.left.domain()[0],
    right: valueScales.right.domain()[1] - valueScales.right.domain()[0],
  };

  const hasRightAxisSeries = data.series.some((series) => series.axis === 'right');

  // Default to right as main axis in case no series on left axis
  const primaryValueScale = domainSizes.left > 0 ? valueScales.left : valueScales.right;

  // Force right axis ticks to align with primary axis ticks
  const rightAxisTicks = convertTicksToScale(
    primaryValueScale.ticks(NumYTicks),
    primaryValueScale,
    valueScales.right,
  );

  const graphHeight = height - GraphVerticalPadding;

  return (
    <div className={commonStyles.graph}>
      <svg width={width} height={graphHeight} className={commonStyles.graphMain}>
        {!hideGrid && (
          <>
            <GridRows
              width={width}
              height={height}
              scale={primaryValueScale}
              tickValues={primaryValueScale.ticks(NumYTicks)}
              stroke={gridLineColor}
            />
            <GridColumns
              width={width}
              height={height}
              scale={zonedDateScale}
              stroke={gridLineColor}
              tickValues={getTickValues(aggPeriod ?? 'hour', zonedDateScale)}
              // eslint-disable-next-line react/no-children-prop
              children={(lines) => renderGridColumns(lines, { width, strokeColor: gridLineColor })}
            />
            <GridColumns
              width={width}
              height={height}
              scale={zonedDateScale}
              stroke={gridHighlightedLineColor}
              tickValues={getHighlightedTickValues(aggPeriod ?? 'hour', zonedDateScale)}
              // eslint-disable-next-line react/no-children-prop
              children={(lines) =>
                renderGridColumns(lines, { width, strokeColor: gridHighlightedLineColor })
              }
            />
          </>
        )}
        {data.series.map(({ key, color }) => (
          <LinearGradient
            key={key}
            id={`gradient-${color}-${key}`}
            from={color}
            to={color}
            fromOpacity={0.8}
            toOpacity={0.2}
          />
        ))}
        {renderSeries()}
        {renderSeriesValueDots()}
        {renderSeriesValues()}
        {hideGrid ? null : (
          <>
            {domainSizes.left > 0 && (
              <AxisLeft
                left={yAxisWidths.left + 4}
                scale={valueScales.left}
                hideTicks
                hideAxisLine
                tickClassName={commonStyles.tickLabel}
                tickLabelProps={() => ({ dx: 4, dy: 10, textAnchor: 'end' })}
                tickValues={valueScales.left.ticks(NumYTicks)}
                tickFormat={(n) => formatLeftAxisValue(n.valueOf())}
              />
            )}
            {domainSizes.right > 0 && hasRightAxisSeries && (
              <AxisRight
                left={width - 4}
                scale={valueScales.right}
                hideTicks
                hideAxisLine
                tickClassName={commonStyles.tickLabel}
                tickLabelProps={() => ({ dx: 0, dy: 10, x: 0, textAnchor: 'end' })}
                tickValues={rightAxisTicks}
                tickFormat={(n) => formatRightAxisValue(n.valueOf())}
              />
            )}
          </>
        )}
      </svg>

      <svg width={width} height={graphHeight} className={commonStyles.graphInteractive}>
        {hasBars && renderHighlightedPeriod()}
        {tooltipData && (
          <>
            {hasOnlyBars ? null : (
              <Line
                from={{ x: dateScale(tooltipData.date), y: 0 }}
                to={{ x: dateScale(tooltipData.date), y: height }}
                stroke={hoverLineColor}
                strokeWidth={1}
                pointerEvents="none"
                strokeDasharray="5,2"
              />
            )}
            {tooltipData.points.toReversed().map((point, i) => {
              if (point.date === null || point.value === null || point.chartType === 'bar') {
                return null;
              }

              return (
                <circle
                  key={i}
                  cx={dateScale(point.date)}
                  cy={valueScales[point.axis](point.value ?? 0)}
                  r={5}
                  fill={point.color}
                  stroke="white"
                  strokeOpacity={0.2}
                  strokeWidth={1}
                  pointerEvents="none"
                />
              );
            })}
          </>
        )}
        <Bar
          {...InteractableProps}
          width={width}
          height={height}
          fill="transparent"
          onTouchStart={handleMouseMove}
          onTouchMove={handleMouseMove}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => {
            setTooltipData(null);
            onDateHighlight(null);
          }}
          onClick={(e) => {
            if (tooltipData === null || groupKey === undefined) {
              return;
            }
            const { clientX, clientY } = e;
            const date = tooltipData.date.toISOString();
            const series = data.series[0];
            onTimeClick?.({
              x: clientX,
              y: clientY,
              key: isCombinedKey(series.key) ? explodeCombinedKey(series.key).valueKey : series.key,
              groupFilters: [
                {
                  key: groupKey,
                  value: date,
                  precision: aggPeriod ?? undefined,
                },
              ],
            });
          }}
        />
      </svg>

      {tooltipData && <Tooltip tooltipData={tooltipData} onDismiss={() => setTooltipData(null)} />}
    </div>
  );
};

interface TooltipData {
  date: Date;
  position: { x: number; y: number };
  timezone: string;
  aggPeriod: TimeAggregationPeriod | null;
  hoveredSeriesKey?: string;
  points: {
    label: string;
    value: number | null;
    type: model.PropertyType | null;
    format?: common.PropertyValueFormat;
    date: Date;
    color: string;
    axis: 'left' | 'right';
    chartType: ChartType;
    isHovered?: boolean;
  }[];
}

const Tooltip = (props: { tooltipData: TooltipData; onDismiss?: () => void }) => {
  const { tooltipData, onDismiss } = props;

  if (tooltipData.points.length === 0) {
    return null;
  }

  const totalValueType = first(tooltipData.points)?.type ?? 'Number';
  const totalValueFormat = tooltipData.points.every(
    (point) => point.format === first(tooltipData.points)?.format,
  )
    ? first(tooltipData.points)?.format
    : undefined;

  return (
    <ChartTooltip
      position={tooltipData.position}
      onDismiss={onDismiss}
      title={getFormattedDateForPeriod(
        tooltipData.date,
        tooltipData.aggPeriod,
        tooltipData.timezone,
      )}>
      <ul>
        {tooltipData.points.length > 1 && (
          <li>
            Total:{' '}
            <strong>
              {formatPropertyValue(
                tooltipData.points
                  .map((point) => point.value)
                  .filter(notNil)
                  .reduce((sum, value) => sum + value, 0),
                { type: totalValueType, format: totalValueFormat },
              )}
            </strong>
          </li>
        )}
        {sortTooltipData(tooltipData)
          .points.slice(0, TooltipItemsLimit)
          .map((point, i) => (
            <ChartTooltipItem
              key={i}
              label={point.label ?? '-'}
              value={formatPropertyValue(point.value, { type: point.type, format: point.format })}
              color={point.color}
              isHovered={point.isHovered}
            />
          ))}
        {tooltipData.points.length > TooltipItemsLimit && (
          <li className={commonStyles.tooltipLimit}>
            {tooltipData.points.length - TooltipItemsLimit} more...
          </li>
        )}
      </ul>
    </ChartTooltip>
  );
};
